import { useLayoutEffect, useState } from 'react';

// A popover placed from where its button actually is, not from a breakpoint
// (2026-09-28): the Sales header wraps differently at every width, so
// "right-aligned under the button" ran off the left edge on a phone. Under
// the button, right edges lined up, then clamped so it never leaves the
// window. Returns a style for a position: fixed popover (null until placed).
//
// Flips (Nic 2026-10-23): pass the popover's own ref and it opens ABOVE the
// button when it won't fit below and there's more room above, and back
// below when that changes (a row near the bottom of the window).
const GUTTER = 16;
const GAP = 8;

const useAnchoredPopover = (open, anchorRef, width, popRef = null) => {
  const [style, setStyle] = useState(null);
  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return undefined;
    }
    const place = () => {
      const b = anchorRef.current?.getBoundingClientRect();
      if (!b) return;
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      const w = Math.min(width, vw - GUTTER * 2);
      const left = Math.min(Math.max(GUTTER, b.right - w), vw - w - GUTTER);
      const below = vh - b.bottom - GAP - GUTTER;
      const above = b.top - GAP - GUTTER;
      // Its full height, even while a max height clips it.
      const h = popRef?.current ? popRef.current.scrollHeight : 0;
      const up = !!h && h > below && above > below;
      const next = up
        ? { position: 'fixed', top: Math.max(GUTTER, b.top - GAP - h), left, width: w, maxHeight: Math.max(160, above), overflowY: 'auto' }
        : { position: 'fixed', top: b.bottom + GAP, left, width: w, maxHeight: Math.max(160, below), overflowY: 'auto' };
      // Not measured yet: hold it invisible for that one frame, so it never
      // shows below and then jumps up.
      if (popRef && !h) next.visibility = 'hidden';
      setStyle((cur) =>
        cur &&
        cur.top === next.top &&
        cur.left === next.left &&
        cur.width === next.width &&
        cur.maxHeight === next.maxHeight &&
        cur.visibility === next.visibility
          ? cur
          : next,
      );
    };
    place();
    // Once it's on screen its height is known: place it again (flip if needed),
    // and again whenever its content changes size.
    const raf = requestAnimationFrame(place);
    let ro = null;
    if (popRef && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(place);
      const watch = () => popRef.current && ro.observe(popRef.current);
      watch();
      requestAnimationFrame(watch);
    }
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, width, popRef]);
  return style;
};

export default useAnchoredPopover;
